<template>
  <div>
    <div
      v-if="isWholeAdvReportingPageLoading"
      class="d-flex flex-column justify-content-center align-content-center mt-4 rounded-30 text-center p-5 gap-4"
      data-test="c-spinner"
    >
      <div class="d-flex justify-content-center align-content-center">
        <CSpinner color="primary" />
      </div>
      <p>Just a moment, we are calculating your requested data...</p>
    </div>
    <div v-else class="d-flex flex-column gap-3">
      <Loader
        :is-processing="isDateDependentChartDataLoading"
        loader-text="Just a moment, we are calculating your requested data..."
        data-test="loader"
      />

      <PageHeader data-test="page-header" />

      <ErrorHandler
        v-if="error != null || state.chartSettingError != null"
        :error="error || state.chartSettingError"
        data-test="error-handler"
        @done="
          () => {
            if (error) setError(null)
            if (state.chartSettingError) state.chartSettingError = null
          }
        "
      />

      <div class="advanced-reporting-view" data-test="advance-reporting-view">
        <div v-if="isKpiEnabled" class="kpi-section" data-test="kpi-section">
          <KpiCard
            v-for="kpi in kpiData"
            :key="kpi.title"
            :title="kpi.title"
            :value="kpi.value"
            :trend="kpi.trend"
          />
        </div>

        <div class="charts-grid charts-section" data-test="charts-section">
          <Chart
            v-for="chart in dateIndependentCharts"
            :key="`${chart.id}`"
            :chart="chart"
          />
        </div>

        <DateFilter data-test="date-filter" />

        <template v-if="dateDependentCharts.length > 0">
          <div class="charts-grid charts-section" data-test="charts-section">
            <Chart
              v-for="chart in dateDependentCharts"
              :key="`${chart.id}`"
              :chart="chart"
              :is-date-dependent="true"
            />
          </div>
        </template>
        <template v-else>
          <div class="no-charts-section" data-test="no-charts-section">
            <p>No charts available</p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import advanceReportingConfigs from "@/configs/advanceReportingConfigs"
import advanceReportingConstants from "@/constants/advanceReportingConstants"
import { invalidateAdvanceReportingCacheIfSchoolChanged } from "@/store/modules/axiousWithCache"
import axios from "axios"
import Chart from "@/v3components/AdvanceReporting/Chart.vue"
import DateFilter from "@/v3components/AdvanceReporting/DateFilter.vue"
import KpiCard from "@/v3components/AdvanceReporting/KpiCard.vue"
import PageHeader from "@/v3components/AdvanceReporting/PageHeader.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import ErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import { computed, onMounted, onUnmounted, provide, reactive } from "vue"
import { useStore } from "vuex"

export default {
  name: "AdvanceReporting",
  components: {
    PageHeader,
    KpiCard,
    Loader,
    ErrorHandler,
    Chart,
    DateFilter
  },
  setup() {
    const store = useStore()
    const cancelTokenSource = axios.CancelToken.source()

    provide("advanceReportingCancelToken", cancelTokenSource.token)

    const state = reactive({
      isSchoolListLoading: false,
      isChartSettingLoading: false,
      chartSettingError: null
    })
    const isAdvanceReportingPageLoading = computed(
      () =>
        store?.state?.advanceReportingStore?.isAdvanceReportingPageLoading ||
        false
    )
    const isDateDependentChartDataLoading = computed(
      () =>
        store?.state?.advanceReportingStore?.isDateDependentChartDataLoading ||
        false
    )
    const isWholeAdvReportingPageLoading = computed(
      () =>
        isAdvanceReportingPageLoading.value ||
        state.isChartSettingLoading ||
        state.isSchoolListLoading
    )
    const error = computed(
      () => store?.state?.advanceReportingStore?.error || null
    )
    const activeSchool = computed(() => schoolList?.value?.[0] || null)
    const schoolList = computed(
      () => store?.getters?.["advanceReportingStore/schoolList"] || []
    )
    const selectedSchools = computed(() => {
      return store?.state?.advanceReportingStore?.selectedSchools || []
    })
    const defaultSchoolList = computed(() => {
      // If user has access to multiple schools, default to "All Schools"
      if (schoolList?.value?.length > 1) {
        return (
          schoolList?.value?.map((school) => ({
            id: school.id,
            name: school.name
          })) || []
        )
      }
      // If only one school, default to that school
      if (activeSchool.value) {
        return [
          {
            id: activeSchool.value.id,
            name: activeSchool.value.name
          }
        ]
      }
      return []
    })
    const configurableChartList = computed(() => {
      return (
        store?.getters?.["advanceReportingStore/configurableChartList"] || []
      )
    })
    const chartSettings = computed(() => {
      return store?.getters?.["advanceReportingStore/chartSettings"] || []
    })
    const chartSettingsMap = computed(() => {
      const map = {}
      if (chartSettings.value.length === 0) {
        configurableChartList.value.forEach((chart) => {
          map[chart.feChartId] = {
            index: chart.index,
            status: chart.status
          }
        })
      } else {
        chartSettings.value.forEach((setting) => {
          map[setting.feChartId] = {
            index: setting.index,
            status: setting.status
          }
        })
      }
      return map
    })
    const isKpiEnabled = computed(() =>
      advanceReportingConfigs.isChartEnabled(
        advanceReportingConstants.DEFAULT_CHART_IDS.USERS_KPI
      )
    )

    const kpiData = computed(() => [
      {
        title: "Admins",
        value:
          store?.getters?.["advanceReportingStore/kpiData"]?.totalAdmins || 0,
        trend: `${store?.getters?.["advanceReportingStore/kpiData"]?.adminTrend || 0}%`
      },
      {
        title: "Teachers",
        value:
          store?.getters?.["advanceReportingStore/kpiData"]?.totalTeachers || 0,
        trend: `${store?.getters?.["advanceReportingStore/kpiData"]?.teacherTrend || 0}%`
      },
      {
        title: "Staff",
        value:
          store?.getters?.["advanceReportingStore/kpiData"]?.totalStaff || 0,
        trend: `${store?.getters?.["advanceReportingStore/kpiData"]?.staffTrend || 0}%`
      },
      {
        title: "Students",
        value:
          store?.getters?.["advanceReportingStore/kpiData"]?.totalStudents || 0,
        trend: `${store?.getters?.["advanceReportingStore/kpiData"]?.studentTrend || 0}%`
      }
    ])

    const dateIndependentCharts = computed(() => {
      const allCharts = [
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS
            .SCHOOL_YEAR_PASS_BREAKDOWN,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS
                .SCHOOL_YEAR_PASS_BREAKDOWN
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS
                .SCHOOL_YEAR_PASS_BREAKDOWN
            ],
          type: "pie",
          colSpan: 2,
          inverted: false,
          slots: {
            footer: {
              legends: true
            }
          }
        },
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS
            .SCHOOL_YEAR_PASS_TREND,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_TREND
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_TREND
            ],
          type: "line",
          colSpan: 4,
          inverted: false,
          slots: {
            header: {
              comparisonLegends: true
            }
          }
        }
      ]
      // Filter by feature flags
      return allCharts.filter((chart) =>
        advanceReportingConfigs.isChartEnabled(chart.id)
      )
    })

    const dateDependentCharts = computed(() => {
      const allCharts = [
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS.PASS_BREAKDOWN,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS.PASS_BREAKDOWN
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS.PASS_BREAKDOWN
            ],
          type: "pie",
          colSpan: 2,
          inverted: false,
          slots: {
            footer: {
              legends: true
            }
          }
        },
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS.PASS_TREND,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS.PASS_TREND
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS.PASS_TREND
            ],
          type: "line",
          colSpan: 4,
          inverted: false,
          slots: {
            header: {
              comparisonLegends: true
            }
          }
        },
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS
            .TOTAL_PASSES_BY_SCHOOLS,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS
                .TOTAL_PASSES_BY_SCHOOLS
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS
                .TOTAL_PASSES_BY_SCHOOLS
            ],
          type: "column",
          colSpan: 6,
          inverted: false,
          slots: {
            header: {
              chartTableToggle: true
            }
          }
        },
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_TAKEN,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_TAKEN
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_TAKEN
            ],
          type: "column",
          colSpan: 3,
          inverted: true,
          slots: {
            header: {
              revealNameToggle: true
            },
            footer: {
              viewMoreToggle: true
            }
          }
        },
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_GRANTED,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_GRANTED
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_GRANTED
            ],
          type: "column",
          colSpan: 3,
          inverted: true,
          slots: {
            header: {
              revealNameToggle: true
            },
            footer: {
              viewMoreToggle: true
            }
          }
        },
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS
            .PASS_BREAKDOWN_BY_GRAD_YEAR,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS
                .PASS_BREAKDOWN_BY_GRAD_YEAR
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS
                .PASS_BREAKDOWN_BY_GRAD_YEAR
            ],
          type: "column",
          colSpan: 3,
          inverted: false
        },
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS.AVERAGE_PASS_DURATION,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS.AVERAGE_PASS_DURATION
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS.AVERAGE_PASS_DURATION
            ],
          type: "column",
          colSpan: 3,
          inverted: false
        },
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS
            .MOST_FREQUENTED_LOCATIONS,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS
                .MOST_FREQUENTED_LOCATIONS
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS
                .MOST_FREQUENTED_LOCATIONS
            ],
          type: "column",
          colSpan: 3,
          inverted: true,
          slots: {
            footer: {
              viewMoreToggle: true
            }
          }
        },
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS.TARDY_INFRACTIONS,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS.TARDY_INFRACTIONS
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS.TARDY_INFRACTIONS
            ],
          type: "line",
          colSpan: 3,
          inverted: false,
          slots: {
            header: {
              comparisonLegends: true
            }
          }
        },
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS.CONTACT_CONTROL,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS.CONTACT_CONTROL
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS.CONTACT_CONTROL
            ],
          type: "column",
          colSpan: 3,
          inverted: true,
          slots: {
            header: {
              revealNameToggle: true
            },
            footer: {
              viewMoreToggle: true
            }
          }
        },
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS.VISITORS_TREND,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS.VISITORS_TREND
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS.VISITORS_TREND
            ],
          type: "line",
          colSpan: 3,
          inverted: false,
          slots: {
            header: {
              comparisonLegends: true
            }
          }
        },
        {
          id: advanceReportingConstants.DEFAULT_CHART_IDS.ZERO_GRANTERS,
          title:
            advanceReportingConstants.CHART_ID_VS_TITLE[
              advanceReportingConstants.DEFAULT_CHART_IDS.ZERO_GRANTERS
            ],
          information:
            advanceReportingConstants.CHART_ID_VS_INFORMATION[
              advanceReportingConstants.DEFAULT_CHART_IDS.ZERO_GRANTERS
            ],
          type: "line",
          colSpan: 3,
          inverted: false,
          slots: {
            header: {
              comparisonLegends: true
            }
          }
        }
      ]

      // Effective selected school IDs (for school-specific chart overrides)
      const effectiveSchoolIds =
        (selectedSchools.value?.length > 0
          ? selectedSchools.value
          : defaultSchoolList.value
        )
          ?.map((s) => s?.id)
          .filter(Boolean) || []

      // Filter by feature flags first (with school overrides when schools selected)
      let filteredCharts = allCharts.filter((chart) =>
        advanceReportingConfigs.isChartEnabled(chart.id, effectiveSchoolIds)
      )

      // Filter and order logic based on backend chartSettings
      if (Object.keys(chartSettingsMap.value).length > 0) {
        filteredCharts = filteredCharts.filter((chart) => {
          const settings = chartSettingsMap.value[chart.id]
          return settings ? settings.status : true
        })
        filteredCharts.sort((a, b) => {
          const aSettings = chartSettingsMap.value[a.id]
          const bSettings = chartSettingsMap.value[b.id]
          const aIndex = aSettings?.index ?? 999
          const bIndex = bSettings?.index ?? 999
          return aIndex - bIndex
        })
      }

      return filteredCharts
    })

    const setError = (error) => {
      store.commit("advanceReportingStore/setError", error)
    }

    const getSchoolList = async () => {
      try {
        state.isSchoolListLoading = true
        await store.dispatch("advanceReportingStore/fetchSchoolList", {
          cancelToken: cancelTokenSource.token
        })
      } catch (err) {
        if (!axios.isCancel(err)) {
          setError(err)
        }
      } finally {
        state.isSchoolListLoading = false
      }
    }

    const getChartSettings = async () => {
      try {
        state.isChartSettingLoading = true
        await store.dispatch("advanceReportingStore/fetchChartSettings", {
          cancelToken: cancelTokenSource.token
        })
      } catch (error) {
        if (!axios.isCancel(error)) {
          state.chartSettingError = error
        }
      } finally {
        state.isChartSettingLoading = false
      }
    }

    const handleInitialSchoolSetup = async () => {
      const schoolsToSet =
        selectedSchools.value.length > 0
          ? selectedSchools.value
          : defaultSchoolList.value
      if (schoolsToSet.length > 0) {
        try {
          setError(null)
          store?.commit(
            "advanceReportingStore/setSelectedSchools",
            schoolsToSet
          )
          await store?.dispatch(
            "advanceReportingStore/fetchAdvanceReportingPageData",
            { cancelToken: cancelTokenSource.token }
          )
        } catch (err) {
          if (!axios.isCancel(err)) {
            setError(err)
          }
        }
      }
    }

    onMounted(async () => {
      const activeSchoolId = store.getters["schools/activeSchool"]?.id
      await invalidateAdvanceReportingCacheIfSchoolChanged(activeSchoolId)

      await getSchoolList()
      await getChartSettings()
      await handleInitialSchoolSetup()
    })

    onUnmounted(() => {
      cancelTokenSource.cancel("Component unmounted")
    })

    return {
      state,
      isKpiEnabled,
      kpiData,
      isDateDependentChartDataLoading,
      isWholeAdvReportingPageLoading,
      error,
      dateIndependentCharts,
      dateDependentCharts,
      setError
    }
  }
}
</script>
